/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Full-bleed Finder hero. Lazy-loads Cover Flow; reduced-motion / no-WebGL
 * uses the 2D grid inside HomeFinderWindow.
 */

import { lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import Box from '@mui/material/Box';
import { getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ThreeSceneShell } from '@/components/ThreeSceneShell';
import { useThreeScene } from '@/hooks/useThreeScene';
import { useRecentScrobbles } from '@/hooks/useRecentScrobbles';
import { trpc } from '@/lib/trpc';
import { analytics } from '@/lib/analytics';
import {
  aboutFinderItems,
  blogFinderItems,
  connectFinderItems,
  homeFinderSearch,
  musicLikedItems,
  musicPlaylistItems,
  musicRecentsItems,
  prodengFinderItems,
  projectFinderItems,
  quoteFinderItems,
  usesFinderItems,
  type FinderFolderId,
  type FinderItem,
  type MusicView,
} from '@/lib/homeFinder';
import { HomeFinderWindow } from './HomeFinderWindow';

const loadHomeFinderScene = () =>
  import('./HomeFinderScene').then((mod) => ({ default: mod.HomeFinderScene }));
const HomeFinderScene = lazy(loadHomeFinderScene);
if (typeof window !== 'undefined') void loadHomeFinderScene();

const homeRoute = getRouteApi('/');

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useLayoutEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return reduced;
}

export function HomeHero() {
  const router = useRouter();
  const navigate = useNavigate({ from: '/' });
  const search = homeRoute.useSearch();
  const folder: FinderFolderId = search.folder ?? 'blog';
  const musicView: MusicView = folder === 'music' ? (search.view ?? 'recents') : 'recents';
  const reducedMotion = usePrefersReducedMotion();
  const { hasWebGL, isMounted } = useThreeScene();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [heroInView, setHeroInView] = useState(true);
  const heroRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerId: number; startX: number; startIndex: number; moved: boolean } | null>(null);
  const selectedIndexRef = useRef(0);
  const browseMethodsRef = useRef(new Set<'keys' | 'wheel'>());

  const latestQuery = useQuery(trpc.blog.latest.queryOptions({ limit: 8 }));
  const libraryQuery = useQuery(trpc.spotify.libraryTeaser.queryOptions());
  const scrobbles = useRecentScrobbles('cdbattags', 10);

  const items: FinderItem[] = useMemo(() => {
    switch (folder) {
      case 'blog':
        return blogFinderItems(latestQuery.data?.posts);
      case 'prodeng':
        return prodengFinderItems();
      case 'music':
        if (musicView === 'playlists') return musicPlaylistItems(libraryQuery.data?.playlists ?? []);
        if (musicView === 'liked') return musicLikedItems(libraryQuery.data?.liked ?? []);
        return musicRecentsItems(scrobbles.data ?? []);
      case 'projects':
        return projectFinderItems();
      case 'uses':
        return usesFinderItems();
      case 'about':
        return aboutFinderItems();
      case 'quotes':
        return quoteFinderItems();
      case 'connect':
        return connectFinderItems();
      default:
        return [];
    }
  }, [folder, musicView, latestQuery.data, libraryQuery.data, scrobbles.data]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [folder, musicView]);

  useEffect(() => {
    setSelectedIndex((index) => Math.min(index, Math.max(0, items.length - 1)));
  }, [items.length]);

  useEffect(() => {
    if (!isMounted) return;
    analytics.homeFinderOpened(reducedMotion || !hasWebGL ? 'fallback' : 'webgl');
  }, [isMounted, hasWebGL, reducedMotion]);

  const queryOriginRef = useRef(search);
  useEffect(() => {
    const origin = queryOriginRef.current;
    if (origin.folder) analytics.homeFinderFolderSelected(origin.folder, 'query');
    if (origin.folder === 'music' && origin.view) analytics.homeFinderMusicView(origin.view);
  }, []);

  useEffect(() => {
    const node = heroRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroInView(entry.isIntersecting),
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const selected = items[selectedIndex];
  selectedIndexRef.current = selectedIndex;

  const markBrowsed = useCallback((method: 'keys' | 'wheel') => {
    if (browseMethodsRef.current.has(method)) return;
    browseMethodsRef.current.add(method);
    analytics.homeFinderBrowsed(method);
  }, []);

  const openItem = useCallback(
    (item = selected) => {
      if (!item) return;
      analytics.homeFinderItemOpened(folder, item.id, item.href);
      if (folder === 'music' && musicView === 'recents' && item.external) {
        analytics.lastfmTrackClicked(item.title, item.href);
      }
      if (folder === 'music' && (musicView === 'playlists' || musicView === 'liked') && item.external) {
        analytics.outboundLinkClicked(item.href, 'home-finder-spotify');
      }
      if (folder === 'connect' && item.external) {
        analytics.outboundLinkClicked(item.href, 'home-connect');
      }
      if (item.external) {
        window.open(item.href, '_blank', 'noopener,noreferrer');
        return;
      }
      router.history.push(item.href);
    },
    [folder, musicView, router, selected],
  );

  const handleFolder = useCallback(
    (next: FinderFolderId) => {
      if (next === folder) return;
      analytics.homeFinderFolderSelected(next, 'click');
      void navigate({
        search: homeFinderSearch(next, next === 'music' ? musicView : undefined),
        resetScroll: false,
      });
    },
    [folder, musicView, navigate],
  );

  const handleMusicView = useCallback(
    (view: MusicView) => {
      if (view === musicView) return;
      analytics.homeFinderMusicView(view);
      void navigate({
        search: homeFinderSearch('music', view),
        resetScroll: false,
      });
    },
    [musicView, navigate],
  );

  const onFlowPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startIndex: selectedIndex,
      moved: false,
    };
  };

  const onFlowPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const delta = event.clientX - drag.startX;
    if (Math.abs(delta) > 8) drag.moved = true;
    const next = drag.startIndex - Math.round(delta / 88);
    setSelectedIndex(Math.max(0, Math.min(items.length - 1, next)));
  };

  const onFlowPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!drag.moved) {
      openItem();
    }
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setSelectedIndex((index) => Math.max(0, index - 1));
        markBrowsed('keys');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setSelectedIndex((index) => Math.min(items.length - 1, index + 1));
        markBrowsed('keys');
      } else if (event.key === 'Enter') {
        event.preventDefault();
        openItem();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items.length, openItem, markBrowsed]);

  const useCanvas = isMounted && hasWebGL && !reducedMotion;
  const showGrid = isMounted && (!hasWebGL || reducedMotion);
  const spinNowPlaying = folder === 'music' && musicView === 'recents' && Boolean(items[0]?.nowPlaying);
  const frameloop = useCanvas && spinNowPlaying && heroInView ? 'always' : 'demand';

  useLayoutEffect(() => {
    if (!useCanvas) return;
    const node = heroRef.current;
    if (!node) return;
    let acc = 0;
    const STEP = 72;
    const onWheel = (event: WheelEvent) => {
      if (items.length < 2) return;
      const axis =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      const pixels =
        event.deltaMode === 1 ? axis * 16 : event.deltaMode === 2 ? axis * window.innerHeight : axis;
      if (pixels === 0) return;
      const dir = Math.sign(pixels);
      const index = selectedIndexRef.current;
      const last = items.length - 1;
      const canMove = (dir > 0 && index < last) || (dir < 0 && index > 0);
      if (!canMove) return;
      event.preventDefault();
      acc += pixels;
      if (Math.abs(acc) < STEP) return;
      const step = Math.sign(acc);
      acc = 0;
      setSelectedIndex(Math.max(0, Math.min(last, index + step)));
      markBrowsed('wheel');
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [useCanvas, items.length, markBrowsed]);

  return (
    <Box
      ref={heroRef}
      tabIndex={0}
      aria-label="Site Finder"
      sx={{
        position: 'relative',
        height: 'calc(100dvh - var(--site-header-height))',
        bgcolor: '#1e293b',
        outline: 'none',
      }}
    >
      {useCanvas && (
        <ThreeSceneShell
          clientReady
          fallback={null}
          sx={{ position: 'absolute', inset: 0 }}
        >
          <HomeFinderScene
            items={items}
            selectedIndex={selectedIndex}
            spinNowPlaying={spinNowPlaying && heroInView}
            frameloop={frameloop}
            sceneKey={`${folder}-${musicView}`}
          />
        </ThreeSceneShell>
      )}

      <HomeFinderWindow
        folder={folder}
        onFolder={handleFolder}
        musicView={musicView}
        onMusicView={handleMusicView}
        items={items}
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
        onOpen={() => openItem()}
        onFlowPointerDown={onFlowPointerDown}
        onFlowPointerMove={onFlowPointerMove}
        onFlowPointerUp={onFlowPointerUp}
        reducedMotion={showGrid}
        pending={!showGrid && !useCanvas}
      />
    </Box>
  );
}
